import { NavLink } from "react-router-dom";
import {
  LayoutDashboard,
  Building2,
  Users2,
  Boxes,
  ChevronRight,
  CreditCard,
  BarChart3,
  Settings2,
  MessageSquare,
  ToggleRight,
  Activity,
  ScrollText,
  ClipboardList,
  ClipboardCheck,
  AlertOctagon,
  Sparkles,
  PlayCircle,
  CalendarClock,
  Zap,
  Bell,
  ShieldAlert,
  Award,
  Wrench,
  FileText,
  Bot,
  Package,
  GraduationCap,
  Network,
  History,
  LifeBuoy,
  HelpCircle,
  Plug,
  Fingerprint,
  AlarmClock,
} from "lucide-react";
import { TID } from "@/constants/testIds";
import { useAuth } from "@/lib/auth";
import { getPerms } from "@/lib/permissions";
import { Trophy } from "lucide-react";

const Section = ({ title, children }) => (
  <div className="px-3 pt-6 pb-1">
    <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-emerald-400/70 font-mono">
      {title}
    </div>
    <div className="mt-2 flex flex-col gap-0.5">{children}</div>
  </div>
);

const Item = ({ to, icon: Icon, label, testId }) => (
  <NavLink
    to={to}
    end
    data-testid={testId}
    className={({ isActive }) =>
      [
        "group flex items-center gap-3 px-3 py-2 text-sm font-body transition-colors",
        isActive
          ? "bg-emerald-900/40 text-emerald-300 font-semibold border-r-4 border-emerald-500 rounded-l-md"
          : "text-stone-400 hover:text-stone-100 hover:bg-stone-900/60 rounded-md",
      ].join(" ")
    }
  >
    <Icon className="h-4 w-4 shrink-0" />
    <span className="flex-1">{label}</span>
    <ChevronRight className="h-3.5 w-3.5 opacity-0 group-hover:opacity-60 transition-opacity" />
  </NavLink>
);

// FASE 20 — nav content shared by the desktop aside and the mobile drawer
export function SidebarNav({ onNavigate }) {
  const { user, company, impersonation } = useAuth();
  const isSuper = user?.role === "super_admin";
  const p = getPerms(user?.role);
  // FASE 20 — while suspended, only the financial/support areas are reachable
  const suspended = company?.status === "suspended" && !isSuper && !impersonation;

  if (suspended) {
    return (
      <div className="flex flex-col h-full" onClick={onNavigate}>
        <div className="flex items-center gap-2 px-5 py-5 border-b border-stone-900" data-testid={TID.sidebarLogo}>
          <img src="/logo.png" alt="Ultra" className="h-8 w-8 rounded-md" />
          <div>
            <div className="font-display text-lg font-extrabold tracking-tight leading-none">Ultra</div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-emerald-400/80 font-body">Cozinha organizada</div>
          </div>
        </div>
        <nav className="flex-1 px-3 py-4 space-y-1" data-testid="sidebar-suspended-nav">
          {user?.role === "company_admin" && (
            <Item to="/billing" icon={CreditCard} label="Planos & Cobrança" testId={TID.pricing} />
          )}
          <Item to="/support" icon={LifeBuoy} label="Suporte" testId="nav-support" />
        </nav>
        <div className="px-5 py-4 border-t border-stone-900 text-[10px] text-rose-400 font-body">
          Assinatura suspensa
        </div>
      </div>
    );
  }

  const operacao = [
    p.dashboard && <Item key="dash" to="/dashboard" icon={LayoutDashboard} label="Dashboard" testId={TID.dashboard} />,
    p.corporate && <Item key="corp" to="/corporate" icon={Network} label="Dashboard Corporativo" testId="corporate-nav" />,
    p.checklists && <Item key="chk" to="/checklists" icon={ClipboardCheck} label="Checklists" testId="checklists-nav" />,
    p.executions && <Item key="exe" to="/executions" icon={PlayCircle} label="Execuções" testId="executions-nav" />,
    p.schedules && <Item key="sch" to="/schedules" icon={CalendarClock} label="Agendamentos" testId="schedules-nav" />,
    p.occurrences && <Item key="occ" to="/occurrences" icon={AlertOctagon} label="Ocorrências" testId="occurrences-nav" />,
    p.rules && <Item key="rul" to="/rules" icon={Zap} label="Regras" testId="rules-nav" />,
    p.notifications && <Item key="not" to="/notifications" icon={Bell} label="Notificações" testId="notifications-nav" />,
  ].filter(Boolean);

  const equipePonto = [
    p.timeclock && <Item key="tclk" to="/timeclock" icon={Fingerprint} label="Ponto" testId="timeclock-nav" />,
    p.timeclockAdmin && <Item key="tclka" to="/timeclock/admin" icon={AlarmClock} label="Gestão de Ponto" testId="timeclock-admin-nav" />,
    p.teamRanking && <Item key="rank" to="/team-ranking" icon={Trophy} label="Ranking da Equipe" testId="team-ranking-nav" />,
    p.users && <Item key="usr" to="/users" icon={Users2} label="Usuários" testId={TID.users} />,
  ].filter(Boolean);

  const qualidade = [
    p.appcc && <Item key="appcc" to="/appcc" icon={ShieldAlert} label="APPCC" testId="appcc-nav" />,
    p.audits && <Item key="aud" to="/audits" icon={Award} label="Auditorias" testId="audits-nav" />,
    p.trainings && <Item key="trn" to="/trainings" icon={GraduationCap} label="Treinamento" testId="trainings-nav" />,
    p.equipment && <Item key="eqp" to="/equipment" icon={Wrench} label="Equipamentos" testId="equipment-nav" />,
    p.reports && <Item key="rep" to="/reports" icon={FileText} label="Relatórios" testId="reports-nav" />,
  ].filter(Boolean);

  // Módulo Controle de Estoque — gated pela feature flag (ausente = ativa)
  const stockModuleOn = (company?.feature_flags || {}).stock_control !== false;
  const controleEstoque = stockModuleOn
    ? [
        p.sanitary && <Item key="san" to="/sanitary" icon={Package} label="Estoque" testId="sanitary-nav" />,
        p.consumptions && <Item key="con" to="/stock-consumptions" icon={ClipboardCheck} label="Consumos" testId="stock-consumptions-nav" />,
      ].filter(Boolean)
    : [];

  return (
    <div className="flex flex-col h-full" onClick={onNavigate}>
      <div className="flex items-center gap-2 px-5 py-5 border-b border-stone-900" data-testid={TID.sidebarLogo}>
        <img src="/logo.png" alt="Ultra" className="h-8 w-8 rounded-md" />
        <div>
          <div className="font-display text-lg font-extrabold tracking-tight leading-none">
            Ultra
          </div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-emerald-400/80 font-body">
            Cozinha organizada
          </div>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto scroll-thin">
        {isSuper && (
          <Section title="Backoffice SaaS">
            <Item to="/admin/metrics" icon={BarChart3} label="Métricas" testId={TID.adminMetrics} />
            <Item to="/admin/companies" icon={Building2} label="Empresas" testId={TID.adminCompanies} />
            <Item to="/admin/plans" icon={Boxes} label="Planos" testId={TID.adminPlans} />
            <Item to="/admin/subscriptions" icon={ClipboardList} label="Assinaturas" testId={TID.adminSubscriptions} />
            <Item to="/admin/payments" icon={CreditCard} label="Pagamentos" testId={TID.adminPayments} />
            <Item to="/admin/leads" icon={Sparkles} label="Leads" testId="admin-leads-nav" />
            <Item to="/admin/tickets" icon={MessageSquare} label="Suporte" testId={TID.adminTickets} />
            <Item to="/admin/feature-flags" icon={ToggleRight} label="Feature Flags" testId={TID.companyFlags} />
            <Item to="/admin/audit" icon={ScrollText} label="Auditoria" testId={TID.adminAudit} />
            <Item to="/admin/support-sessions" icon={History} label="Sessões de Suporte" testId="admin-support-sessions-nav" />
            <Item to="/admin/monitoring" icon={Activity} label="Monitoramento" testId={TID.adminMonitoring} />
          </Section>
        )}

        {operacao.length > 0 && <Section title="Operação">{operacao}</Section>}

        {equipePonto.length > 0 && <Section title="Equipe & Ponto">{equipePonto}</Section>}

        {controleEstoque.length > 0 && (
          <Section title="Estoque & Suprimentos">{controleEstoque}</Section>
        )}

        {qualidade.length > 0 && <Section title="Qualidade & Auditoria">{qualidade}</Section>}

        {p.chatbot && (
          <Section title="Assistentes">
            <Item to="/chatbot" icon={Bot} label="WhatsApp" testId="chatbot-nav" />
          </Section>
        )}

        <Section title="Ajuda">
          <Item to="/help" icon={HelpCircle} label="Me ajude" testId="help-nav" />
        </Section>

        {p.support && (
          <Section title="Suporte">
            <Item to="/support" icon={LifeBuoy} label="Suporte" testId="support-nav" />
          </Section>
        )}

        <Section title="Conta & Sistema">
          {p.units && <Item to="/units" icon={Building2} label="Unidades" testId={TID.units} />}
          {p.pricing && (
            <Item to="/billing" icon={CreditCard} label="Planos & Cobrança" testId={TID.pricing} />
          )}
          {p.integrations && (
            <Item to="/integrations" icon={Plug} label="Integrações" testId="integrations-nav" />
          )}
          {p.settings && (
            <Item to="/settings" icon={Settings2} label="Configurações" testId={TID.settings} />
          )}
        </Section>
      </div>

      <div className="px-5 py-4 border-t border-stone-900 text-[10px] text-stone-500 font-body">
        Ultra · Cozinha organizada
      </div>
    </div>
  );
}

export default function Sidebar() {
  return (
    <aside
      data-testid={TID.sidebar}
      className="hidden md:flex md:flex-col w-64 shrink-0 bg-stone-950 text-stone-100 min-h-screen border-r border-stone-900"
    >
      <SidebarNav />
    </aside>
  );
}
